import { FormControl, MenuItem, Select, Stack, TextField, Typography } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { Controller, get } from "react-hook-form";
import useCountryList from "src/hooks/address/useCountryList";
import FieldLabel from "./FieldLabel";

const VIETNAM_PHONE_CODE = "+84";
const escapeRegExp = (value) => String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const parsePhoneValue = (rawValue, fallbackPhoneCode = VIETNAM_PHONE_CODE, preferredPhoneCodes = []) => {
  const normalized = String(rawValue ?? "").trim();
  if (!normalized) {
    return {
      phoneCode: fallbackPhoneCode,
      phoneNumber: "",
    };
  }

  const cleaned = normalized.replace(/\s+/g, "");

  const preferredMatchedCode = preferredPhoneCodes
    .filter(Boolean)
    .sort((a, b) => b.length - a.length)
    .find((code) => cleaned.startsWith(code));
  if (preferredMatchedCode) {
    return {
      phoneCode: preferredMatchedCode,
      phoneNumber: cleaned.slice(preferredMatchedCode.length).replace(/\D/g, ""),
    };
  }

  if (!cleaned.startsWith("+")) {
    return {
      phoneCode: fallbackPhoneCode,
      phoneNumber: cleaned.replace(/\D/g, ""),
    };
  }

  const match = cleaned.match(/^(\+\d{1,3})(\d*)$/);
  if (match) {
    return {
      phoneCode: match[1],
      phoneNumber: match[2] || "",
    };
  }

  return {
    phoneCode: fallbackPhoneCode,
    phoneNumber: cleaned.replace(/\D/g, ""),
  };
};

// Component to render the country select
const CountrySelect = ({ value, onChange, width = "40px", offsetTop = 12 }) => {
  const { data: countries } = useCountryList({ limit: 300, skip: 0, sortField: "name", sortOrder: "asc" });
  const [activeFlag, setActiveFlag] = useState();
  const countryList = useMemo(() => (Array.isArray(countries?.data) ? countries.data : []), [countries?.data]);
  const hasMatchingPhoneCode = countryList.some((country) => country.phoneCode === value);
  const safeValue = hasMatchingPhoneCode ? value : "";

  const handleChangeCountry = (e) => {
    const flag = countries?.data?.find((country) => country.phoneCode === e.target?.value)?.flag;
    setActiveFlag(flag);
    onChange?.(e);
  };

  useEffect(() => {
    const flag = countryList.find((country) => country.phoneCode === safeValue)?.flag;
    setActiveFlag(flag);
  }, [countryList, safeValue]);

  return (
    <FormControl>
      <Select
        value={safeValue}
        onChange={handleChangeCountry}
        sx={{
          width: "40px",
          maxWidth: "40px",
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "inherit",
            borderWidth: "0px !important",
          },
        }}
        renderValue={() => (
          <Stack
            direction="row"
            alignItems="center"
            gap="2px"
            width="fit-content"
          >
            {activeFlag}
          </Stack>
        )}
        SelectDisplayProps={{ style: { padding: 0, display: "flex", alignItems: "center", justifyContent: "center" } }}
        IconComponent={"div"}
        maxRows={10}
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: "300px",
              width: width,
              marginTop: offsetTop,
              marginLeft: -8,
            },
          },
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "left",
          },
          transformOrigin: {
            vertical: "top",
            horizontal: "left",
          },
        }}
        size="small"
      >
        <MenuItem value="">
          <Typography color="text.secondary">-</Typography>
        </MenuItem>
        {countryList.map((country) => (
          <MenuItem
            key={country._id}
            value={country?.phoneCode}
            sx={{ display: "flex", alignItems: "center", gap: "6px" }}
          >
            <Typography>{country.flag}</Typography>
            <Typography
              maxWidth="180px"
              noWrap
            >
              {country.name}
            </Typography>
            <Typography>({country.phoneCode})</Typography>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

// Component to render the phone number input
const PhoneNumberInputComponent = ({
  value,
  defaultValue,
  onChange,
  defaultPhoneCode = VIETNAM_PHONE_CODE,
  error,
  helperText,
  label,
  required,
  phoneCode: phoneCodeProps,
  ...props
}) => {
  const initialParsed = parsePhoneValue(value ?? defaultValue, defaultPhoneCode, [phoneCodeProps, defaultPhoneCode]);
  const [phoneCode, setPhoneCode] = useState(initialParsed.phoneCode);
  const [phoneNumber, setPhoneNumber] = useState(initialParsed.phoneNumber);
  const [containerWidth, setContainerWidth] = useState(null);
  const [containerHeight, setContainerHeight] = useState(null);
  const ref = useRef(null);
  const containerRef = useRef(null);
  const lastEmittedValueRef = useRef(null);
  const shouldRefocusPhoneInputRef = useRef(false);

  const handleChangePhoneCode = (e) => {
    const nextCode = e.target.value;
    shouldRefocusPhoneInputRef.current = true;
    setPhoneCode(nextCode);
    const nextValue = `${nextCode}${phoneNumber}`;
    if (String(value ?? "") !== nextValue) {
      lastEmittedValueRef.current = nextValue;
      onChange?.(nextValue);
    }
  };

  useEffect(() => {
    if (!shouldRefocusPhoneInputRef.current) return;
    requestAnimationFrame(() => {
      ref.current?.focus();
      shouldRefocusPhoneInputRef.current = false;
    });
  }, [phoneCode]);

  const handleChangeValue = (e) => {
    const rawInput = String(e.target.value ?? "");
    const prefixRegex = new RegExp(`^\\(${escapeRegExp(phoneCode)}\\)\\s*`);
    const phoneOnlyInput = rawInput.replace(prefixRegex, "");
    const nextPhoneNumber = phoneOnlyInput.replace(/\D/g, "");
    const regex = /^\d{0,10}$/;
    if (!regex.test(nextPhoneNumber)) return;
    setPhoneNumber(nextPhoneNumber);
    const nextValue = `${phoneCode}${nextPhoneNumber}`;
    if (String(value ?? "") !== nextValue) {
      lastEmittedValueRef.current = nextValue;
      onChange?.(nextValue);
    }
  };

  const handleFocusOut = () => {
    if (phoneCode === VIETNAM_PHONE_CODE) {
      setPhoneNumber((prev) => {
        let phoneNumber = prev;
        if (prev.length === 10 && prev[0] === "0") {
          phoneNumber = prev.slice(1);
        }
        const nextValue = `${phoneCode}${phoneNumber}`;
        if (String(value ?? "") !== nextValue) {
          lastEmittedValueRef.current = nextValue;
          onChange?.(nextValue);
        }
        return phoneNumber;
      });
    }
  };

  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth;
        const height = containerRef.current.offsetHeight;
        setContainerWidth(width);
        setContainerHeight(height);
      }
    };

    updateSize();

    const resizeObserver = new ResizeObserver(updateSize);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    const externalValue = String(value ?? "");
    if (lastEmittedValueRef.current === externalValue) {
      lastEmittedValueRef.current = null;
      return;
    }

    const currentValue = `${phoneCode}${phoneNumber}`;
    if (externalValue === currentValue) return;

    const parsed = parsePhoneValue(value, defaultPhoneCode, [phoneCodeProps, phoneCode, defaultPhoneCode]);
    setPhoneCode((prev) => (prev === parsed.phoneCode ? prev : parsed.phoneCode));
    setPhoneNumber((prev) => (prev === parsed.phoneNumber ? prev : parsed.phoneNumber));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, defaultPhoneCode, phoneCodeProps]);

  useEffect(() => {
    if (!phoneCodeProps) return;
    setPhoneCode(phoneCodeProps);
  }, [phoneCodeProps]);

  return (
    <TextField
      {...props}
      inputRef={ref}
      ref={containerRef}
      fullWidth
      label={
        label != null && label !== "" ? (
          <FieldLabel
            label={label}
            required={required}
          />
        ) : (
          props.label
        )
      }
      slotProps={{
        input: {
          sx: { pl: 1 },
          startAdornment: (
            <CountrySelect
              value={phoneCode}
              onChange={handleChangePhoneCode}
              width={containerWidth}
              offsetTop={containerHeight / 2}
            />
          ),
        },
      }}
      value={`(${phoneCode}) ${phoneNumber}`}
      onChange={handleChangeValue}
      onBlur={handleFocusOut}
      error={!!error}
      helperText={helperText}
    />
  );
};

/**
 * If control is provided, use Controller to control the value
 *
 * Otherwise, use the PhoneNumberInputComponent to render the input
 * @param {*} param0
 * @returns
 */
const PhoneNumberInput = ({ control, name, defaultValue, defaultPhoneCode, label, required, onChange, value, ...props }) => {
  if (!control)
    return (
      <PhoneNumberInputComponent
        defaultValue={defaultValue}
        defaultPhoneCode={defaultPhoneCode}
        onChange={onChange}
        value={value}
        label={label}
        required={required}
        {...props}
      />
    );

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, formState: { errors } }) => {
        const error = get(errors, name);
        return (
          <PhoneNumberInputComponent
            value={field.value}
            onChange={field.onChange}
            defaultValue={defaultValue}
            defaultPhoneCode={defaultPhoneCode}
            error={error}
            helperText={error?.message}
            label={label}
            required={required}
            {...props}
          />
        );
      }}
    />
  );
};

export default PhoneNumberInput;
